import { useState, useRef, useEffect } from 'react';
import {
  ArrowUpTrayIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CircularProgress } from '@mui/material';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter, InputBox } from '../FormComponent';
import {
  generateUploadUrl,
  uploadFileToS3,
  registerExecutionFile,
} from '../../utils/api/executionFileApis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import {
  MAX_FILE_SIZE,
  ALLOWED_MIME_TYPES,
  ALLOWED_EXTENSIONS,
  ACCEPTED_FILE_TYPE_LABELS,
} from './constants';
import { formatFileSize } from './helpers';
import FileTypeChip from './FileTypeChip';
import styles from '../../styles/assetsManagement/filesManager.module.scss';

/**
 * Upload flow:
 *   1. User selects file + enters name/description → clicks Upload
 *   2. Generate presigned URL → upload to S3 quarantine
 *   3. ScanningFilesMonitor (parent) tracks scan via SSE
 *   4. Modal reflects status; grid updates when scan completes
 */
const UploadFileModal = ({ folder, fileScanStatus, onUploadComplete, onClose }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileNameError, setFileNameError] = useState('');
  const [description, setDescription] = useState('');
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState('');

  // Steps: idle → uploading → scanning → saving → done | scan_error
  const [uploadStep, setUploadStep] = useState('idle');
  const [progress, setProgress] = useState(0);

  const [scanError, setScanError] = useState(null);

  const inputRef = useRef();
  const { showSnackbar } = useSnackbar();

  const isUploading = uploadStep === 'uploading';
  const isScanning = uploadStep === 'scanning';
  const isBusy = isUploading || isScanning;

  // Grid-level ScanningFilesMonitor tracks scan status; modal reflects it via fileScanStatus.
  useEffect(() => {
    if (!isScanning || !fileScanStatus || fileScanStatus === 'SCANNING') return;

    if (fileScanStatus === 'CLEAN') {
      setUploadStep('done');
      return;
    }
    if (fileScanStatus === 'INFECTED') {
      setScanError('This file contains a virus and has been removed.');
      setUploadStep('scan_error');
      return;
    }
    if (fileScanStatus === 'ERROR') {
      setScanError('Scan encountered an error. Please try uploading again.');
      setUploadStep('scan_error');
    }
  }, [isScanning, fileScanStatus]);

  const validateFile = (f) => {
    if (!ALLOWED_MIME_TYPES.includes(f.type))
      return `File type not allowed. Accepted: ${ALLOWED_EXTENSIONS}`;
    if (f.size > MAX_FILE_SIZE)
      return `File is too large (${formatFileSize(f.size)}). Maximum is 5 MB.`;
    return null;
  };

  const selectFile = (f) => {
    const err = validateFile(f);
    setFileError(err || '');
    if (!err) {
      setFile(f);
      setFileName((prev) => prev.trim() ? prev : f.name);
    } else {
      setFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) selectFile(dropped);
  };

  const handleUpload = async () => {
    if (!file || !folder) return;

    const trimmedName = fileName.trim();
    if (!trimmedName) { setFileNameError('File name is required'); return; }
    if (trimmedName.length > 200) { setFileNameError('File name must be 200 characters or fewer'); return; }
    setFileNameError('');

    try {
      // Step 1: Generate presigned URL
      setUploadStep('uploading');
      setProgress(10);
      const urlRes = await generateUploadUrl({ fileName: file.name, fileType: file.type, fileSize: file.size });
      if (urlRes.status !== STATUS.SUCCESS) {
        showSnackbar(urlRes.error || 'Failed to generate upload URL', 'error');
        setUploadStep('idle');
        return;
      }
      const { uploadUrl, fields, fileKey } = urlRes.data;
      setProgress(40);

      // Step 2: Upload to S3 quarantine
      const s3Res = await uploadFileToS3({ uploadUrl, fields, file });
      if (!s3Res.success) {
        showSnackbar(s3Res.error || 'S3 upload failed', 'error');
        setUploadStep('idle');
        return;
      }
      setProgress(100);

      // Step 3: Create DB record immediately (SCANNING) so user can navigate away
      const fileMeta = file ? { size: file.size, mimeType: file.type } : undefined;
      const registerRes = await registerExecutionFile({
        tempFileKey: fileKey,
        folderId: folder.id,
        fileName: fileName.trim(),
        fileDescription: description.trim() || undefined,
        fileMetadata: fileMeta,
      });
      if (registerRes.status !== STATUS.SUCCESS) {
        showSnackbar(registerRes.error || 'Failed to register file for scanning', 'error');
        setUploadStep('idle');
        return;
      }

      const created = registerRes.data;
      // Register in File Manager list; ScanningFilesMonitor handles scan + finalize.
      onUploadComplete(created, { closeModal: false });

      // Step 4: Enter scanning state — File Manager monitor tracks the quarantine key
      setUploadStep('scanning');
    } catch {
      showSnackbar('Unexpected error during upload', 'error');
      setUploadStep('idle');
    }
  };

  const dropZoneClass = [
    styles.dropZone,
    dragging ? styles.dragging : '',
    file ? styles.hasFile : '',
    fileError ? styles.hasError : '',
  ].filter(Boolean).join(' ');

  const getFooterButtons = () => {
    if (uploadStep === 'scan_error') {
      return [
        { label: 'Close', variant: 'primary', className: 'white bg-blue600', onClick: onClose },
      ];
    }
    if (uploadStep === 'done') {
      return [
        { label: 'Close', variant: 'primary', className: 'white bg-blue600', onClick: onClose },
      ];
    }
    return [
      { label: 'Cancel', variant: 'outline', className: 'primary', onClick: () => {
          if (isScanning) {
            showSnackbar('Your file is being scanned. You can check the status in File Manager.', 'info', 5000);
          }
          onClose();
        } },
      {
        label: isUploading ? 'Uploading…' : 'Upload',
        variant: 'primary',
        className: 'white bg-blue600',
        onClick: handleUpload,
        disabled: !file || isBusy || !!fileError || !fileName.trim(),
        loading: isUploading,
      },
    ];
  };

  return (
    <ModalWrapper
      isOpen
      onClose={() => {
        if (isScanning) {
          showSnackbar('Your file is being scanned. You can check the status in File Manager.', 'info', 5000);
        }
        onClose();
      }}
      title={`Upload File${folder ? ` — ${folder.folderName}` : ''}`}
      size="medium"
      footer={<CommonFooter buttons={getFooterButtons()} />}
    >
      <div className={styles.uploadBody}>
        {/* Scanning / Saving state — blocks the user */}
        {isScanning && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 12, padding: '32px 0',
          }}>
            <ShieldCheckIcon style={{ width: 48, height: 48, color: '#2563eb' }} />
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
              Scanning for viruses…
            </p>
            <CircularProgress size={28} sx={{ color: '#2563eb' }} />
            <p style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center', maxWidth: 320 }}>
              This scan will continue in the background if you close this window.
            </p>
          </div>
        )}

        {uploadStep === 'done' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 12, padding: '32px 0',
          }}>
            <ShieldCheckIcon style={{ width: 48, height: 48, color: '#16a34a' }} />
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#166534' }}>
              Scan complete
            </p>
            <p style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
              You can view the latest status in File Manager.
            </p>
          </div>
        )}

        {/* Scan error / infected */}
        {uploadStep === 'scan_error' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 12, padding: '32px 0',
          }}>
            <ExclamationTriangleIcon style={{ width: 48, height: 48, color: '#dc2626' }} />
            <p style={{ fontSize: '1rem', fontWeight: 600, color: '#dc2626' }}>Upload Failed</p>
            <p style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
              {scanError}
            </p>
          </div>
        )}

        {/* Normal upload form — hidden during scanning/saving/error */}
        {!isScanning && uploadStep !== 'scan_error' && uploadStep !== 'done' && (
          <>
            <div
              className={dropZoneClass}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => !file && inputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !file && inputRef.current?.click()}
              aria-label="Drop file here or press Enter to browse"
            >
              <input
                ref={inputRef}
                type="file"
                accept={ALLOWED_EXTENSIONS}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) selectFile(f);
                  // Allow picking the same file again: browsers often won't fire `onChange`
                  // if the input's value doesn't change.
                  e.target.value = '';
                }}
              />

              {file ? (
                <div className={styles.selectedFileCard}>
                  <div className={styles.fileIconBox}>
                    <DocumentIcon />
                  </div>
                  <div className={styles.selectedFileInfo}>
                    <p className={styles.sfName} title={file.name}>{file.name}</p>
                    <p className={styles.sfSize}>{formatFileSize(file.size)}</p>
                  </div>
                  <FileTypeChip mimeType={file.type} fileName={file.name} />
                  {!isUploading && (
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setFileError('');
                        setFileName('');
                        setFileNameError('');
                        setDescription('');
                        setUploadStep('idle');
                        setProgress(0);
                        setScanError(null);
                        if (inputRef.current) inputRef.current.value = '';
                      }}
                      title="Remove file"
                      aria-label="Remove selected file"
                    >
                      <XMarkIcon />
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <ArrowUpTrayIcon className={styles.dropIcon} />
                  <p className={styles.dropTitle}>
                    Drop a file here, or <span>browse</span>
                  </p>
                  <p className={styles.dropHint}>Max 5 MB · {ALLOWED_EXTENSIONS}</p>
                </>
              )}
            </div>

            {fileError && (
              <p className={styles.fileError}>
                <ExclamationCircleIcon />
                {fileError}
              </p>
            )}

            {isUploading && (
              <div className={styles.progressWrap}>
                <p className={styles.progressLabel}>Uploading to storage…</p>
                <div className={styles.progressTrack}>
                  <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            <InputBox
              label="File name"
              value={fileName}
              onChange={(e) => { setFileName(e.target.value); if (fileNameError) setFileNameError(''); }}
              placeholder="e.g. Sprint 1 execution report.pdf"
              disabled={isUploading}
              maxLength={200}
              error={!!fileNameError}
              helperText={fileNameError}
              required
            />

            <InputBox
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this file…"
              multiline
              rows={2}
              disabled={isUploading}
              maxLength={500}
            />

            <div className={styles.allowedRow}>
              <p className={styles.allowedLabel}>Accepted file types</p>
              <div className={styles.allowedChips}>
                {ACCEPTED_FILE_TYPE_LABELS.map((label) => (
                  <FileTypeChip key={label} fileName={`.${label.toLowerCase()}`} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </ModalWrapper>
  );
};

export default UploadFileModal;
